'use client';

import { useEffect, type ReactNode, useState } from 'react';
import { cn, tv } from '../_lib/utils';
import type { SanityImage } from '../types';
import { Button, ButtonCtaAction, type ButtonCtaActionProps, ComponentButton, type ComponentButtonProps } from '../Inputs/Button';
import { Container } from '../Container';
import { IconResolver } from '../IconResolver';
import { LabelLink, type LabelLinkProps } from '../LabelLink';
import { Text } from '../Text';
import { Video } from '../Video';
import type { Themes } from '../Theme';

const styles = tv({
	slots: {
		base: '',
		chip: 'py-1 flex items-center first:opacity-100 last:after:hidden after:bg-current after:mx-1.5 after:h-[0.1875rem] after:w-[0.1875rem] after:rounded-full',
	},
	variants: {
		layout: {
			page: {
				base: 'py-responsive-xl',
			},
			popup: {
				base: 'pb-responsive-xl',
			},
		},
	},
});

export type ProjectHeroProps = {
	autoplayVideo?: string;
	buttons?: Array<ComponentButtonProps | ButtonCtaActionProps>;
	className?: string;
	copy?: ReactNode;
	image?: SanityImage;
	label?: Pick<LabelLinkProps, 'copy' | 'href' | 'icon'>;
	meta?: string[];
	title?: string;
	showShareButton?: boolean;
	metaLayout?: 'inline' | 'block';
	video?: string;
	linkCopiedText?: string;
	linkCopyFailedText?: string;
	theme?: Themes;
};

export function ProjectHero(props: ProjectHeroProps) {
	const showShareButton = props.showShareButton ?? true;
	const metaLayout = props.metaLayout ?? 'inline';

	const { base, chip } = styles({});

	const [copyUrlActive, setCopyUrlActive] = useState(false);
	const [copyUrlMessage, setCopyUrlMessage] = useState('');

	const linkCopiedText = props.linkCopiedText ?? 'Link copied to clipboard';
	const linkCopyFailedText = props.linkCopyFailedText ?? 'Oh no, something went wrong, please try again or copy the URL manually!';

	function copyUrlToClipboard() {
		if (!navigator.clipboard) {
			return;
		}

		navigator.clipboard.writeText(window.location.href).then(
			() => {
				setCopyUrlActive(true);
				setCopyUrlMessage(linkCopiedText);
			},
			() => {
				setCopyUrlActive(true);
				setCopyUrlMessage(linkCopyFailedText);
			},
		);
	}

	useEffect(() => {
		if (copyUrlActive) {
			setTimeout(() => {
				setCopyUrlActive(false);
			}, 1200);
		}
	}, [copyUrlActive]);

	return (
		<article
			className={cn(base({ className: props.className }), 'pb-responsive-5xl pt-24 md:pt-36 lg:pt-[9rem]')}
			data-component='ProjectHero'
			data-theme={props.theme}
		>
			<Container className='grid gap-spacing-xl items-center lg:grid-cols-2'>
				<div className={`flex flex-col gap-responsive-md lg:pt-responsive-xl lg:pr-responsive-md`}>
					<div className='flex flex-col gap-4'>
						{props.label && <LabelLink {...props.label} size='md' styleType='brand' />}

						{props.title && (
							<Text as='h1' styleType='heading-lg'>
								{props.title}
							</Text>
						)}
					</div>

					{props.copy && <Text styleType='editorial-lg'>{props.copy}</Text>}

					<div className={cn('flex gap-4', metaLayout === 'block' ? 'flex-col justify-center' : 'items-center')}>
						{((props.buttons && props.buttons.length > 0) || showShareButton) && (
							<div className={`flex-none flex flex-wrap gap-4 items-center`}>
								{props.buttons?.map((item, index) => {
									if ('ctaAction' in item) {
										return <ButtonCtaAction key={`${item.label}-${index}-project-hero`} {...item} {...item.buttonProps} />;
									}

									if ('type' in item) {
										return <ComponentButton key={`${item.label}-${index}-project-hero`} {...item} />;
									}

									return null;
								})}

								{showShareButton && (
									<div className={`relative`}>
										<Button
											aria-labelledby='share-copy-url-label'
											parent='ContentHero'
											aria-label={'Copy page url'}
											iconOnly
											styleSize='medium'
											styleType='tertiary'
											onClick={copyUrlToClipboard}
										>
											<IconResolver icon='export' className='w-3.5 h-3.5' />
										</Button>

										<Text
											as='div'
											aria-hidden={!copyUrlActive}
											tabIndex={copyUrlActive ? 0 : -1}
											className={cn(
												'bg-background-primary p-2 rounded-md absolute bottom-[100%] max-w-[20rem] opacity-0 pointer-events-none transition-all duration-slow ease-smooth w-24 text-center max-lg:right-0 lg:left-1/2 lg:-translate-x-1/2',
												copyUrlActive ? 'opacity-100 pointer-events-auto -translate-y-2' : '',
											)}
											id='share-copy-url-label'
											role='tooltip'
											styleType='body-xs-medium'
										>
											{copyUrlMessage}
										</Text>
									</div>
								)}
							</div>
						)}

						{props.meta && props.meta.length > 0 && (
							<ul className={`flex flex-wrap`}>
								{props.meta.map((item, index) => {
									return (
										<Text
											className={`py-1 opacity-80 flex items-center last:after:hidden after:content-[""] after:bg-current after:opacity-80 after:mx-1.5 after:h-[0.1875rem] after:w-[0.1875rem] after:rounded-full`}
											key={`${item}-${index}-project-hero-meta`}
											as='li'
											styleType='chip-md'
										>
											{item}
										</Text>
									);
								})}
							</ul>
						)}
					</div>
				</div>
				<div>
					<Video
						className='px-0 rounded-lg overflow-hidden'
						mediaAspectRatio='5/4'
						autoplayVideo={props.autoplayVideo}
						image={props.image}
						video={props.video}
						size='unset'
						priority
					/>
				</div>
			</Container>
		</article>
	);
}
